// Map free-text court names read off a document onto a dropdown option value.
//
// The previous matcher was a chain of substring checks that returned null for
// anything it did not recognise, and the caller then DISCARDED the extracted
// text — so a perfectly good "SUPERIOR COURT OF CALIFORNIA, COUNTY OF RIVERSIDE
// - CORONA BRANCH" that failed to match simply vanished. This module always
// returns a decision the UI can act on:
//
//   { matched: true,  value, confidence }      -> select that option
//   { matched: false, value: null, extracted } -> KEEP the text and ask the
//                                                 customer to confirm
//
// It never invents a match: a county-only reading maps to that county's MAIN
// courthouse only when no branch is named, and anything ambiguous comes back
// unmatched rather than guessed.
//
// Dependency-free on purpose (same shape as src/lib/uploadLimits.js) so it
// bundles for the browser and runs unchanged under Node in the test suite. The
// option values here are asserted against src/lib/courts.js by
// tests/courtAutofill.test.js, so they cannot drift from the real dropdown.

// Lowercase, strip punctuation, collapse whitespace, and expand the common
// abbreviations that appear on captions and letterheads.
function normalize(text) {
  var s = String(text == null ? "" : text).toLowerCase();
  // Anything that is not a letter, digit or space becomes a space. Written this
  // way deliberately: a punctuation character class containing "/" and brackets
  // is easy to get subtly wrong, and a malformed class silently corrupts how
  // bundlers parse the whole module.
  s = s.replace(/[^a-z0-9 ]+/g, " ");
  s = s.replace(/\bsup(?:er)?\b/g, "superior");
  s = s.replace(/\bsuperior ct\b/g, "superior court");
  s = s.replace(/\bct\b/g, "court");
  s = s.replace(/\bco\b/g, "county");
  s = s.replace(/\bcnty\b/g, "county");
  s = s.replace(/\bdist\b/g, "district");
  s = s.replace(/\bjust\b/g, "justice");
  s = s.replace(/\bbr\b/g, "branch");
  s = s.replace(/\bl a\b/g, "los angeles");
  s = s.replace(/\bs b\b/g, "san bernardino");
  s = s.replace(/\s+/g, " ");
  return s.trim();
}

// County -> the option value for that county's MAIN courthouse.
var COUNTY_MAIN = {
  riverside: "riverside_superior",
  orange: "orange_central",
  "san bernardino": "sbcounty_san_bern",
  "los angeles": "la_central",
};

// Branch / courthouse names and their common aliases -> option value. A named
// branch is stronger evidence than the county alone.
var BRANCH_ALIASES = [
  [["corona"], "riverside_corona"],
  [["banning"], "riverside_banning"],
  [["hemet"], "riverside_hemet"],
  [["indio", "larson"], "riverside_indio"],
  [["murrieta", "southwest"], "riverside_murrieta"],
  [["historic courthouse", "main riverside"], "riverside_superior"],
  [["santa ana", "central justice"], "orange_central"],
  [["fullerton", "north justice"], "orange_north"],
  [["westminster", "west justice"], "orange_west"],
  [["newport beach", "harbor justice"], "orange_harbor"],
  [["rancho cucamonga"], "sbcounty_rancho"],
  [["ontario"], "sbcounty_ontario"],
  [["san bernardino justice", "san bernardino main"], "sbcounty_san_bern"],
  [["pomona"], "la_pomona"],
  [["norwalk"], "la_norwalk"],
  [["west covina"], "la_west_covina"],
  [["stanley mosk", "downtown la"], "la_central"],
];

// Federal courts are distinct from the county superior courts.
var FEDERAL = [
  [["central district of california riverside", "eastern division riverside"], "federal_cd_cal_riverside"],
  [["central district of california", "united states district court"], "federal_cd_cal"],
];

function countyPrefix(value) {
  return String(value).split("_")[0];
}

// Always returns a decision — never a bare null the caller might drop.
function matchCourt(text) {
  var raw = String(text == null ? "" : text).trim();
  var n = normalize(raw);
  function out(value, confidence) { return { matched: true, value: value, confidence: confidence, extracted: raw }; }
  function unmatched(reason) { return { matched: false, value: null, confidence: 0, extracted: raw, reason: reason }; }

  if (!n) return unmatched("empty");

  // Federal first, so "district court" never falls through to a county.
  if (n.indexOf("district court") !== -1 || n.indexOf("united states") !== -1) {
    for (var i = 0; i < FEDERAL.length; i += 1) {
      var faliases = FEDERAL[i][0];
      for (var j = 0; j < faliases.length; j += 1) {
        if (n.indexOf(faliases[j]) !== -1) return out(FEDERAL[i][1], 0.9);
      }
    }
    return unmatched("federal_unrecognized");
  }

  var counties = Object.keys(COUNTY_MAIN).filter(function (c) { return n.indexOf(c) !== -1; });
  var branchHits = BRANCH_ALIASES.filter(function (entry) {
    return entry[0].some(function (a) { return n.indexOf(a) !== -1; });
  });

  if (branchHits.length === 1) {
    var value = branchHits[0][1];
    // When a county is also named, the branch must belong to it.
    if (counties.length === 1 && countyPrefix(value) !== countyPrefix(COUNTY_MAIN[counties[0]])) {
      return unmatched("branch_county_mismatch");
    }
    return out(value, 0.95);
  }
  if (branchHits.length > 1) return unmatched("multiple_branches");

  // A county with no recognisable branch -> that county's main courthouse. This
  // is a weaker signal, so the caller asks the customer to confirm it.
  if (counties.length === 1) return out(COUNTY_MAIN[counties[0]], 0.75);
  if (counties.length > 1) return unmatched("multiple_counties");

  return unmatched("unrecognized");
}

module.exports = {
  normalize: normalize,
  matchCourt: matchCourt,
  COUNTY_MAIN: COUNTY_MAIN,
  BRANCH_ALIASES: BRANCH_ALIASES,
};
